import * as React from 'react';
import * as styled from './styled';
import Modal from 'components/Modal';
import Button, {ButtonVariant} from 'components/Button';
import {SelectCourse} from './components/SelectCourse';
import {InviteModalProps} from './types';
import UserPicker from '../UserPicker';
import {InputVariant} from '../Input';
import {UserObject} from '../../stores/UserStore';
import ControlButton from '../ControlButton';
import {IconTrash} from '../../icons';
import {getImageByPreview} from '../../api/library';
import {inviteStudents} from '../../api/course';
import validateResponse from '../../helpers/validate-response';
import isError from '../../helpers/isError';
import {toast} from 'react-toastify';

/**
 * The modal with invite student in the course content.
 * @param {InviteModalProps} props
 * @return {React.ReactElement<InviteModalProps>}
 * @constructor
 */
export default function InviteModal(
  {
    courseId,
    onSubmit,
    ...modalProps
  }: InviteModalProps,
): React.ReactElement<InviteModalProps> {
  const [
    id, setCourseId,
  ] = React.useState<InviteModalProps['courseId']>(
      courseId,
  );

  const [
    users, setUsers,
  ] = React.useState<UserObject[]>([]);

  /**
   * Render header.
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    return (
      <span>Добавить учеников на курс</span>
    );
  }

  /**
   * Render footer.
   * @return {JSX.Element}
   */
  function renderFooter(): JSX.Element {
    /**
     * This click event handler.
     */
    function handleClick(): void {
      id && (
        inviteStudents(id, users.map((user) => (
          user.id
        ))).then((response) => {
          validateResponse(response);

          if (response && !isError(response)) {
            toast.success('Приглашения отправлены!');
            modalProps.onBlur && modalProps.onBlur();
            onSubmit && onSubmit(users.map((user) => user.id));
          }
        })
      );
    }

    return (
      <Button
        type="button"
        variant={ButtonVariant.Primary}
        disabled={users.length === 0}
        onClick={handleClick}
      >
        Отправить приглашения на курс
      </Button>
    );
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render users.
     * @return {JSX.Element}
     */
    function renderUsers(): JSX.Element {
      /**
       * Render the user.
       * @param {UserObject} user
       * @param {number} index
       * @return {JSX.Element}
       */
      function renderUser(
        user: UserObject,
        index: number,
      ): JSX.Element {
        /**
         * Render delete control.
         * @return {JSX.Element}
         */
        function renderDeleteControl(): JSX.Element {
          /**
           * This click event handler.
           */
          function handleClick(): void {
            setUsers(users.filter((stateUser) => (
              stateUser !== user
            )));
          }

          return (
            <ControlButton onClick={handleClick}>
              <IconTrash />
            </ControlButton>
          );
        }

        return (
          <styled.User key={index}>
            <styled.UserAvatar
              src={getImageByPreview(user.avatar)}
            />
            {user.name}
            {renderDeleteControl()}
          </styled.User>
        );
      }

      return (
        <styled.Users>
          {users.map((user, index) => (
            renderUser(user, index)
          ))}
        </styled.Users>
      );
    }

    /**
     * Render user picker.
     * @return {JSX.Element}
     */
    function renderUserPicker(): JSX.Element {
      /**
       * This select event handler.
       * @param {UserObject} user
       */
      function handleSelect(user: UserObject): void {
        setUsers([
          ...users,
          user,
        ]);
      }

      return (
        <UserPicker
          inputProps={{
            variant: InputVariant.Accent,
            placeholder: 'Введите имя пользователя',
          }}
          onSelect={handleSelect}
        />
      );
    }

    return (
      <styled.Content>
        {renderUserPicker()}
        {renderUsers()}
      </styled.Content>
    );
  }

  return (
    <Modal
      {...modalProps}
      renderHeader={renderHeader()}
      renderFooter={renderFooter()}
      fullScreen
    >
      <SelectCourse
        courseId={id}
        setCourseId={setCourseId}
      />
      {renderContent()}
    </Modal>
  );
}
